/**
 * `Entities_Executor.ts` — port of Unity's `Executor/Entities_Executor.cs`
 * for the web-registered entity tools.
 *
 * Executors resolve hierarchy paths / selection-set names, then dispatch to
 * the owning models (`IProjectModel`, `ISelectionModel`, `IPlacementModel`,
 * `ExplosionModifierModel`) — no scene logic is re-implemented here.
 *
 * Web deviations (documented in the registry): move_parts applies
 * instantly (transition tweening deferred with the gizmo animation work)
 * and `isLocal` is accepted but the web snapshot transform is already
 * entity-local, so scene-space conversion is a no-op for now.
 */

import { Ioc } from "@core/ioc/ioc";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import type { IPlacementModel } from "@models/tools/IPlacementModel";
import type { IExplosionModifierModel } from "@models/modifiers/IExplosionModifierModel";
import type { IUndoRedo } from "@models/project/IUndoRedo";
import {
  BOUNDING_BOX_TOOL_MODEL,
  type IBoundingBoxToolModel,
  type MeasureVisual,
} from "@models/tools/IBoundingBoxToolModel";
import { MEASUREMENTS_MODEL, type IMeasurementsModel } from "@models/tools/IMeasurementsModel";
import {
  KnownUnits,
  formatDistance,
  unitsToScale,
  unitsToShortString,
} from "@models/utils/scaleUnits";
import { explosionPercentageToFactor } from "@models/data/explosionFactor";
import type { EntitySettingsPatch, EntitySnapshotPatch } from "@models/data/entityData";
import type { Vec3 } from "@models/data/entityData";
import {
  PERSISTENT_SPIN_DEFAULT_SPEED_DEG_PER_SEC,
  parsePersistentSpinJson,
  serializePersistentSpin,
  tryNormalizeSpinAxis,
  type PersistentSpinAxis,
  type PersistentSpinData,
} from "@models/data/persistentSpinData";
import {
  DEFAULT_PERSISTENT_HIGHLIGHT,
  PersistentHighlightPulseModes,
  PersistentHighlightSemantics,
  normalizeHighlightColorHex,
  normalizeHighlightPulseMode,
  normalizeHighlightSemantic,
  normalizeHighlightThicknessMode,
  parsePersistentHighlightJson,
  serializePersistentHighlight,
  type PersistentHighlightData,
} from "@models/data/persistentHighlightData";
import {
  animationClipCount,
  getAssetAnimationTakes,
  resolveAnimationTake,
} from "@runtime/assets/animationClips";
import { focusSnapshots } from "@runtime/camera/focusSnapshots";
import {
  isMeasureFailure,
  measureSnapshot,
  MIN_GROUND_RULER_SPAN_M,
} from "@runtime/tools/snapshotMeasure";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import type { AgentToolExecutorHost } from "./AgentToolExecutorModel";
import {
  buildPathIndex,
  displayNameOf,
  isMoved,
  normalizePath,
  resolveManyByPath,
  rootPathOf,
  type PathEntry,
} from "@ai/context/hierarchyPaths";
import {
  getBool,
  getNumber,
  getString,
  getStringList,
  getVector3,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

/** Unity `!string.IsNullOrWhiteSpace` — "the model actually named this field". */
function isFilled(value: string | null): value is string {
  return value !== null && value.trim().length > 0;
}

export class EntitiesExecutor implements IDomainExecutor {
  private readonly m_project: IProjectModel;
  private readonly m_selection: ISelectionModel;
  private readonly m_placement: IPlacementModel;
  private readonly m_explosion: IExplosionModifierModel;
  private readonly m_undoRedo: IUndoRedo;
  private readonly m_host: AgentToolExecutorHost;

  constructor(
    project: IProjectModel,
    selection: ISelectionModel,
    placement: IPlacementModel,
    explosion: IExplosionModifierModel,
    undoRedo: IUndoRedo,
    host: AgentToolExecutorHost,
  ) {
    this.m_project = project;
    this.m_selection = selection;
    this.m_placement = placement;
    this.m_explosion = explosion;
    this.m_undoRedo = undoRedo;
    this.m_host = host;
  }

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.SelectPart:
        return this.selectPart(args);
      case AgentToolNames.FocusOnPart:
        return this.focusOnPart(args);
      case AgentToolNames.HideParts:
        return this.setVisibility(args, false);
      case AgentToolNames.UnhideParts:
        return this.setVisibility(args, true);
      case AgentToolNames.Isolate:
        return this.isolate(args);
      case AgentToolNames.ClearIsolation:
        return this.clearIsolation();
      case AgentToolNames.ResetPlacement:
        return this.resetPlacement(args);
      case AgentToolNames.ResetScene:
        return this.resetScene();
      case AgentToolNames.MoveParts:
        return this.moveParts(args);
      case AgentToolNames.RenamePart:
        return this.renamePart(args);
      case AgentToolNames.UpdatePartContext:
        return this.updatePartContext(args);
      case AgentToolNames.ToggleModelAnimation:
        return this.toggleModelAnimation(args);
      case AgentToolNames.GetPartTransforms:
        return this.getPartTransforms(args);
      case AgentToolNames.GetPartDimensions:
        return this.getPartDimensions(args);
      case AgentToolNames.ClearBoundingBox:
        return this.clearBoundingBox();
      case AgentToolNames.Explode:
      case AgentToolNames.UnexplodeModel:
        return this.explode(toolName, args);
      case AgentToolNames.HighlightPart:
        return this.highlightPart(args);
      case AgentToolNames.ClearHighlight:
        return this.clearHighlight(args);
      case AgentToolNames.ConstantSpinPart:
        return this.constantSpin(args);
      case AgentToolNames.ClearConstantSpin:
        return this.clearConstantSpin(args);
      default:
        return null;
    }
  }

  // -- Target resolution ----------------------------------------------------

  private get sceneId(): string | null {
    return this.m_project.activeScene?.id ?? null;
  }

  private resolveTargets(
    args: ToolArgs,
    pathsKey: string,
    selectionSetKeys: ReadonlyArray<string>,
  ): { entries: PathEntry[]; error: string | null } {
    const sceneId = this.sceneId;
    if (!sceneId) return { entries: [], error: "error: no active scene" };
    const entities = this.m_project.getEntities(sceneId);

    const entries: PathEntry[] = [];

    const paths = getStringList(args, pathsKey);
    if (paths.length > 0) {
      entries.push(...resolveManyByPath(entities, paths));
      if (entries.length === 0) {
        return {
          entries: [],
          error: `error: no parts found at path(s) ${paths.map((p) => `'${p}'`).join(", ")}. Resolve targets with Get_Scene_Context first.`,
        };
      }
    }

    for (const key of selectionSetKeys) {
      for (const setName of getStringList(args, key)) {
        const sets = this.m_project.getSelectionSets(sceneId);
        const set = sets.find((s) => s.Name.toLowerCase() === setName.toLowerCase());
        if (!set) {
          return { entries: [], error: `error: selection set '${setName}' not found` };
        }
        const index = buildPathIndex(entities);
        for (const snapshotId of set.EntitySnapshotIDs ?? []) {
          const entry = index.find((e) => e.snapshot.id === snapshotId);
          if (entry) entries.push(entry);
        }
      }
    }

    return { entries, error: null };
  }

  private static formatTargets(entries: ReadonlyArray<PathEntry>): string {
    return entries
      .map((e) => `name:${displayNameOf(e.snapshot)} path:${e.path}`)
      .join("; ");
  }

  // -- Tools ------------------------------------------------------------

  private async selectPart(args: ToolArgs): Promise<string> {
    const { entries, error } = this.resolveTargets(args, "partPathsToSelect", [
      "selectionSetNameToSelect",
    ]);
    if (error) return error;
    if (entries.length === 0) return "error: no parts found to select";
    this.m_selection.set(entries.map((e) => e.snapshot.id));
    return `ok: ${EntitiesExecutor.formatTargets(entries)}`;
  }

  private async focusOnPart(args: ToolArgs): Promise<string> {
    const { entries, error } = this.resolveTargets(args, "partPathsToFocus", [
      "selectionSetNamesToFocus",
    ]);
    if (error) return error;
    if (entries.length === 0) return "error: no parts found to focus on";

    const useIsolate = getBool(args, "useIsolate") ?? false;
    if (useIsolate) {
      const sceneId = this.sceneId;
      if (sceneId) {
        await this.m_project.updateSceneIsolationMode(
          sceneId,
          entries.map((e) => e.snapshot.id),
        );
      }
    }

    // Shared with the context menu's Focus and the `F` shortcut. Glides (1 s /
    // EaseOutCubic), like Unity's `sceneComponent.Focus`: both clients return
    // the tool result immediately and let the camera finish travelling.
    const result = focusSnapshots(entries.map((e) => e.snapshot.id));
    if (result === "camera-unavailable") {
      return useIsolate
        ? `ok: isolated ${entries.length} part(s); camera unchanged (viewport unavailable)`
        : "tool_error: focus_on_part_camera_unavailable. The 3D viewport is not active.";
    }
    if (result === "no-bounds") {
      return "tool_error: focus_on_part_camera_unavailable. Target has no visible geometry to frame.";
    }
    this.m_selection.set(entries.map((e) => e.snapshot.id));
    return useIsolate
      ? `ok: isolated and focused on ${entries.length} part(s)`
      : `ok: focused on ${entries.length} part(s)`;
  }

  private async setVisibility(args: ToolArgs, visible: boolean): Promise<string> {
    const suffix = visible ? "Unhide" : "Hide";
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const all = getBool(args, visible ? "unhideAllParts" : "hideAllParts") ?? false;
    let entries: PathEntry[];
    if (all) {
      entries = buildPathIndex(this.m_project.getEntities(sceneId)).filter((e) => e.isRoot);
    } else {
      const resolved = this.resolveTargets(args, `partPathsTo${suffix}`, [
        `selectionSetNamesTo${suffix}`,
      ]);
      if (resolved.error) return resolved.error;
      entries = resolved.entries;
    }

    // Media tiles ("text boxes") ride the same tool.
    const tileNames = getStringList(args, `textBoxNamesTo${suffix}`);
    const tiles = this.m_project
      .getMediaTiles(sceneId)
      .filter((t) => tileNames.some((n) => n.toLowerCase() === t.name.toLowerCase()));

    if (entries.length === 0 && tiles.length === 0) {
      return `error: no targets found to ${suffix.toLowerCase()}`;
    }

    await this.m_project.updateEntitySnapshotsAsync(
      sceneId,
      entries.map((entry) => ({
        snapshotId: entry.snapshot.id,
        patch: { visibility: visible },
      })),
    );
    for (const tile of tiles) {
      await this.m_project.updateMediaTileAsync(
        sceneId,
        tile.id,
        { visibility: visible },
        ["Visibility"],
      );
    }

    const parts = EntitiesExecutor.formatTargets(entries);
    const tileNamesOut = tiles.map((t) => `textBox:${t.name}`).join("; ");
    return `ok: ${[parts, tileNamesOut].filter(Boolean).join("; ")}`;
  }

  private async isolate(args: ToolArgs): Promise<string> {
    const { entries, error } = this.resolveTargets(args, "partPathsToIsolate", [
      "selectionSetNamesToIsolate",
    ]);
    if (error) return error;
    if (entries.length === 0) {
      return "error: isolate requires partPathsToIsolate or selectionSetNamesToIsolate";
    }
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";
    await this.m_project.updateSceneIsolationMode(
      sceneId,
      entries.map((e) => e.snapshot.id),
    );
    return `ok: isolated ${EntitiesExecutor.formatTargets(entries)}`;
  }

  private async clearIsolation(): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";
    await this.m_project.updateSceneIsolationMode(sceneId, []);
    return "ok: isolation cleared";
  }

  private async resetPlacement(args: ToolArgs): Promise<string> {
    const { entries, error } = this.resolveTargets(args, "partPathsToReset", [
      "selectionSetNameToReset",
    ]);
    if (error) return error;
    if (entries.length === 0) return "error: no parts found to reset";
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    for (const entry of entries) {
      await this.m_placement.resetTransformAsync(sceneId, entry.snapshot.id);
    }
    return `ok: reset ${EntitiesExecutor.formatTargets(entries)}`;
  }

  private async resetScene(): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";
    // Unity `Entities_Executor` calls
    // `ResetActiveSceneAsync(resetLibraryMaterials: true)` — the shared model
    // API owns the unexplode-then-hard-reset order.
    const result = await this.m_project.resetActiveSceneAsync(true);
    return `ok: scene reset (${result.partsReset} moved part(s) restored)`;
  }

  private async moveParts(args: ToolArgs): Promise<string> {
    const position = getVector3(args, "position");
    const rotation = getVector3(args, "rotation");
    const scale = getVector3(args, "scale");
    if (!position && !rotation && !scale) {
      return "error: move_parts_missing_transform_delta. Provide at least one of position, rotation, or scale.";
    }

    const { entries, error } = this.resolveTargets(args, "partPathsToMove", [
      "selectionSetNameToMove",
    ]);
    if (error) return error;
    if (entries.length === 0) {
      return "error: move_parts_no_targets. Provide partPathsToMove or selectionSetNameToMove resolved via Get_Scene_Context.";
    }

    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";
    const useIncrement = getBool(args, "useIncrement") ?? true;

    for (const entry of entries) {
      const current = entry.snapshot.transform;
      const patch: EntitySnapshotPatch = {
        transform: {
          ...(position
            ? {
                position: (useIncrement
                  ? [
                      current.position[0] + position[0],
                      current.position[1] + position[1],
                      current.position[2] + position[2],
                    ]
                  : position) as Vec3,
              }
            : {}),
          ...(rotation
            ? {
                rotation: (useIncrement
                  ? [
                      current.rotation[0] + rotation[0],
                      current.rotation[1] + rotation[1],
                      current.rotation[2] + rotation[2],
                    ]
                  : rotation) as Vec3,
              }
            : {}),
          ...(scale
            ? {
                scale: (useIncrement
                  ? [
                      current.scale[0] * scale[0],
                      current.scale[1] * scale[1],
                      current.scale[2] * scale[2],
                    ]
                  : scale) as Vec3,
              }
            : {}),
        },
      };
      await this.m_project.updateEntitySnapshotAsync(sceneId, entry.snapshot.id, patch);
    }
    return "ok";
  }

  private async renamePart(args: ToolArgs): Promise<string> {
    const newDisplayName = getString(args, "newDisplayName");
    if (!newDisplayName) return "error: rename_part_missing_newDisplayName";

    const paths = [
      ...getStringList(args, "partPathsToRename"),
      ...(getString(args, "partPath") ? [getString(args, "partPath")!] : []),
    ];
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    let entries = resolveManyByPath(this.m_project.getEntities(sceneId), paths);
    if (entries.length === 0 && this.m_selection.selectedIds.size > 0) {
      const index = buildPathIndex(this.m_project.getEntities(sceneId));
      entries = index.filter((e) => this.m_selection.isSelected(e.snapshot.id));
    }
    if (entries.length === 0) return "error: no parts found to rename";

    for (const entry of entries) {
      await this.m_project.updateEntitySnapshotAsync(sceneId, entry.snapshot.id, {
        displayName: newDisplayName,
      });
    }
    return `ok: renamed ${entries.length} part(s) to '${newDisplayName}'`;
  }

  private async updatePartContext(args: ToolArgs): Promise<string> {
    const newInformation = getString(args, "newInformation");
    if (!newInformation) return "error: update_part_context_missing_newInformation";
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const path = getString(args, "partPath");
    const name = getString(args, "partName");
    const entities = this.m_project.getEntities(sceneId);
    const index = buildPathIndex(entities);

    let entry: PathEntry | null = null;
    if (path) {
      entry = index.find((e) => e.path === normalizePath(path)) ?? null;
      if (!entry) return `error: part not found at path '${path}'`;
    } else if (name) {
      const matches = index.filter(
        (e) => displayNameOf(e.snapshot).toLowerCase() === name.toLowerCase(),
      );
      if (matches.length !== 1) {
        return `error: part name '${name}' matched ${matches.length} parts. Resolve with Get_Scene_Context and pass partPath.`;
      }
      entry = matches[0];
    } else if (this.m_selection.primaryId) {
      entry = index.find((e) => e.snapshot.id === this.m_selection.primaryId) ?? null;
    }
    if (!entry) return "error: no target part for update_part_context";

    await this.m_project.updateEntitySettingsAsync(sceneId, entry.entity.id, {
      description: newInformation,
    });
    return `ok: description updated for name:${displayNameOf(entry.snapshot)} path:${entry.path}`;
  }

  /**
   * The inspector Animation panel, driven by the agent — all five controls.
   *
   * Grown from Unity `Entities_Executor.ToggleModelAnimation` (:3563), which
   * carries `enabled` + `partPath` only. The four playback arguments are a
   * WEB-FIRST addition: upstream registers no tool for take, speed, direction
   * or playback type (`ScriptClassifier.cs:299` even tells its classifier not
   * to assume one exists), so an authored script setting them plays correctly
   * here and DEGRADES on a desktop client, which ignores arguments it does not
   * declare and still applies `enabled`. That graceful degradation is the
   * reason this extends the shared tool id instead of minting a second one.
   *
   * Playback is driven entirely by the PERSISTED settings — there is no
   * transient "play now" path, in either client. `SceneEntityRenderer` diffs
   * `EntityAnimationSettings`, rebuilds the action, and the next frame's
   * visibility edge restarts it; Unity's `AnimationController.Settings` setter
   * likewise applies playback as a side effect of assignment.
   */
  private async toggleModelAnimation(args: ToolArgs): Promise<string> {
    // ⚠ `null` means OMITTED, and omitted must PRESERVE the current autoplay
    // rather than default to false — `enabled` became optional so a caller can
    // change the take or the speed without also asserting play/stop. Defaulting
    // here would silently stop an animation the caller only meant to slow down.
    const enabled = getBool(args, "enabled");
    const partPath = getString(args, "partPath");
    const take = getString(args, "take");
    const speedPercent = getNumber(args, "speedPercent");
    const direction = getString(args, "direction");
    const playback = getString(args, "playback");

    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const entities = this.m_project.getEntities(sceneId);
    const index = buildPathIndex(entities);

    // Any part inside the model is a valid target; it is promoted to its root.
    let target: PathEntry | null = partPath
      ? (resolveManyByPath(entities, [partPath])[0] ?? null)
      : null;
    if (!target && this.m_selection.primaryId) {
      target = index.find((e) => e.snapshot.id === this.m_selection.primaryId) ?? null;
    }
    if (!target) {
      const roots = index.filter((e) => e.isRoot);
      if (roots.length === 0) return "error: scene has no models";
      if (roots.length > 1) return "error: target model not specified (multiple models in scene)";
      target = roots[0];
    }

    const rootPath = rootPathOf(target.path);
    const rootModel = index.find((e) => e.path === rootPath) ?? target;

    if (rootModel.snapshot.isLocked) return "error: model is locked";
    // Unity reads `RuntimeObjectProperties.IsKinematicsEnabled`, which
    // `KinematicsModel` seeds from the root snapshot's `IsCollisionEnabled`.
    if (enabled === true && rootModel.snapshot.isCollisionEnabled) {
      return "error: model has kinematics enabled; animation is not accessible";
    }

    // `null` is UNKNOWN (no viewport, or an undecoded asset), NOT zero — a
    // model whose animation simply is not decoded here must not be refused.
    if (animationClipCount(rootModel.entity.assetId) === 0) {
      return "error: model has no animation";
    }

    const patch: {
      -readonly [K in keyof NonNullable<EntitySettingsPatch["animation"]>]:
        NonNullable<EntitySettingsPatch["animation"]>[K]
    } = {};
    const applied: string[] = [];

    // `applyEntityAnimationSettingsPatch` reads `patch.autoplay ?? current`, so
    // leaving the key off is what preserves it.
    if (enabled !== null) {
      patch.autoplay = enabled;
      applied.push(enabled ? "playing" : "stopped");
    }

    if (take !== null) {
      const takes = getAssetAnimationTakes(rootModel.entity.assetId);
      if (takes === null) {
        // Refusing here would be dishonest in the opposite direction: we cannot
        // resolve a NAME without the decoded clip list, and guessing an index
        // would play an arbitrary animation.
        return "error: this model's animation takes are not loaded yet; retry once the scene has finished loading";
      }
      const resolved = resolveAnimationTake(takes, take);
      if (!resolved.ok) return `error: ${resolved.reason}`;
      // The wire and the desktop client store an INDEX; only the tool's surface
      // is name-facing. `applyEntityAnimationSettingsPatch` parses it back.
      patch.take = String(resolved.index);
      applied.push(`take:'${resolved.label}'`);
    }

    if (speedPercent !== null) {
      if (!(speedPercent >= 0) || speedPercent > 1000) {
        return "error: speedPercent must be between 0 and 1000";
      }
      patch.speedPercent = speedPercent;
      applied.push(`speed:${speedPercent}%`);
    }

    if (direction !== null) {
      const wanted = direction.trim().toLowerCase();
      // `reverse`/`backwards` are what a user says; the stored vocabulary is
      // Unity's `forward`/`backward` (the SIGN of speed, not a field).
      const normalized = wanted === "backward" || wanted === "backwards" || wanted === "reverse"
        ? "backward"
        : wanted === "forward" || wanted === "forwards"
          ? "forward"
          : null;
      if (!normalized) return `error: direction must be forward or backward (got '${direction}')`;
      patch.direction = normalized;
      applied.push(`direction:${normalized}`);
    }

    if (playback !== null) {
      const wanted = playback.trim().toLowerCase().replace(/[_ -]/g, "");
      const normalized = wanted === "once"
        ? "once"
        : wanted === "pingpong"
          ? "pingPong"
          : wanted === "loop"
            ? "loop"
            : null;
      if (!normalized) {
        return `error: playback must be loop, once or pingPong (got '${playback}')`;
      }
      patch.playback = normalized;
      applied.push(`playback:${normalized}`);
    }

    // ⚠ Read BEFORE the write: the renderer's `sameAnimationSettings` gate
    // short-circuits an identical patch, so nothing is disposed, nothing is
    // recreated, no visibility edge fires and the clip keeps running from
    // wherever it was. Reporting `ok` for that would tell the agent something
    // happened when the user saw nothing change.
    // Every control is optional, so a call naming none of them asks for
    // nothing. Answering `ok` would tell the agent an edit happened.
    if (Object.keys(patch).length === 0) {
      return "error: nothing to change - pass at least one of enabled, take, speedPercent, direction or playback";
    }

    const before = this.m_project.getEntityById(sceneId, rootModel.entity.id)?.animationSettings;

    await this.m_project.updateEntitySettingsAsync(sceneId, rootModel.entity.id, {
      animation: patch,
    });

    const after = this.m_project.getEntityById(sceneId, rootModel.entity.id)?.animationSettings;
    const unchanged = before !== undefined
      && after !== undefined
      && before.autoplay === after.autoplay
      && before.playbackType === after.playbackType
      && before.speed === after.speed
      && before.clipIndex === after.clipIndex;

    const name = displayNameOf(rootModel.snapshot);
    const detail = applied.join(", ");
    if (unchanged) {
      return `ok: model '${name}' animation was already ${detail}; nothing changed`;
    }
    return `ok: model '${name}' animation set - ${detail}`;
  }

  private async getPartTransforms(args: ToolArgs): Promise<string> {
    const path = getString(args, "partPath");
    if (!path) return "error: Get_Part_Transforms_missing_partPath";
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const entities = this.m_project.getEntities(sceneId);
    const index = buildPathIndex(entities);
    const entry = index.find((e) => e.path === normalizePath(path));
    if (!entry) return `error: part not found at path '${path}'`;

    const t = entry.snapshot.transform;
    const moved = isMoved(entry.snapshot);
    const payload: Record<string, unknown> = {
      name: displayNameOf(entry.snapshot),
      path: entry.path,
      localPosition: t.position,
      localRotation: t.rotation,
      scale: t.scale,
      isMoved: moved,
    };
    if (moved) {
      payload.originalPosition = entry.snapshot.resetTransform.position;
      payload.originalRotation = entry.snapshot.resetTransform.rotation;
    }
    return JSON.stringify(payload);
  }

  /**
   * Every size and height Spark states comes from here, so the numbers are
   * measured rather than recalled: `snapshotMeasure` reads the same oriented
   * bounds the Measure tool draws, and the figures are pre-formatted in the
   * user's display unit so the agent repeats a string instead of converting
   * one. The box is pinned alongside the answer because "width" and "depth"
   * only mean something once you can see which span each one is.
   *
   * heightAboveGround is the one figure no edge of the box shows, so it gets a
   * visual of its own - a dashed ruler from the part's lowest mesh point
   * straight down to the ground (`BoundingBoxToolController.drawGroundDrop`).
   *
   * ⚠ THE TWO VISUALS ARE EXCLUSIVE, AND THE QUESTION PICKS ONE. A clearance
   * question draws the ruler ALONE (2026-09-01, team feedback: "get rid of the
   * bounding box... only draw a ruler from the lowest point"): the box shows
   * three spans nobody asked about, and its own labels then compete with the
   * clearance pill for the reader's eye. So `showGroundRuler` selects the
   * answer AND its illustration together, and `showBoundingBox` governs the
   * size question only. One call answers size or clearance, never both.
   */
  private async getPartDimensions(args: ToolArgs): Promise<string> {
    const path = getString(args, "partPath");
    if (!path) return "error: Get_Part_Dimensions_missing_partPath";
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const entry = buildPathIndex(this.m_project.getEntities(sceneId)).find(
      (e) => e.path === normalizePath(path),
    );
    if (!entry) return `error: part not found at path '${path}'`;

    const measurement = measureSnapshot(entry.snapshot.id);
    if (isMeasureFailure(measurement)) {
      return measurement === "no-geometry"
        ? "tool_error: Get_Part_Dimensions_no_geometry. This node carries no mesh of its own - measure its parent or a child instead."
        : "tool_error: Get_Part_Dimensions_not_measurable. The part is not currently rendered, so it cannot be measured.";
    }

    const unit = this.displayUnit;
    // One decimal, matching the pills the bounding box draws — an answer that
    // disagreed with the label on screen would read as a second opinion.
    const size = (metres: number) =>
      `${(metres / unitsToScale(unit)).toFixed(1)} ${unitsToShortString(unit)}`;

    // The Answer skill's `Measured in the scene` attribution. Published to the
    // panel rather than left for the model to write: the realtime session emits
    // audio only, so a source line in the reply is a line Spark reads aloud
    // after every measurement.
    this.m_host.conversationPresentation?.publishAttribution("Source: Measured in the scene");

    // Default FALSE, and that direction is the point: the box explains width /
    // height / depth on its own, so the ruler is a different question's
    // picture. Forgetting it costs a height answer its illustration;
    // defaulting it true put a line to the floor under every size question,
    // which is what this parameter exists to stop.
    const isClearanceQuestion = getBool(args, "showGroundRuler") ?? false;
    // ⚠ A CLEARANCE QUESTION DRAWS NO BOX - so `showBoundingBox` is read only
    // for the SIZE question, and is NOT a second knob that can put a box back
    // around a height answer. Deriving the pair here rather than asking the
    // agent for two coordinated flags is what stops a forgotten argument
    // bringing the box back: the model says which question it is answering,
    // and the product decides what that question looks like.
    const wantBox = !isClearanceQuestion && (getBool(args, "showBoundingBox") ?? true);
    // A part resting ON the ground has no span to draw, and the renderer
    // suppresses the line rather than printing a dot under a "0.00 cm" pill.
    // Folded in HERE so the agent is never told a visual is up that the
    // renderer is about to hide - the two read one threshold.
    const wantRuler =
      isClearanceQuestion
      && Math.abs(measurement.heightAboveGround) >= MIN_GROUND_RULER_SPAN_M;
    const visual = this.applyMeasureVisual(entry.snapshot.id, wantBox, wantRuler);
    // ⚠ THE PAYLOAD CARRIES ONLY THE FIGURES FOR THE QUESTION THAT WAS ASKED,
    // and returning all four "because we measured them anyway" is the bug this
    // exists to stop - do not put them back.
    //
    // One call used to return width/height/depth AND heightAboveGround, so a
    // follow-up ("how long is it", then "how high is it") was answerable
    // straight from the transcript. `base.md` tells the agent to reuse rather
    // than refetch, so it did - correctly - and made NO call. But this tool is
    // also what DRAWS, and `setGroundRuler` is only reachable through it, so
    // the previous question's picture stayed up under the new answer. A prompt
    // rule telling it to call anyway held twice and then didn't; a coin-flip is
    // not a fix, so the reuse path is removed instead of argued with.
    //
    // Now `showGroundRuler` selects the answer AND its illustration together:
    // the clearance figure and the line to the floor arrive as one thing, or
    // neither does. Neither question can be answered from the other's result,
    // so the call that redraws cannot be skipped. A compound question costs two
    // calls, which is rare and cheap (measuring is local and synchronous).
    return JSON.stringify({
      name: displayNameOf(entry.snapshot),
      path: entry.path,
      source: "scene geometry",
      ...(isClearanceQuestion
        ? {
            heightAboveGround: formatDistance(measurement.heightAboveGround, unit),
            heightDatum: "ground plane",
          }
        : {
            width: size(measurement.width),
            height: size(measurement.height),
            depth: size(measurement.depth),
          }),
      boundingBoxShown: visual.boxShown,
      groundRulerShown: visual.rulerShown,
    });
  }

  private async clearBoundingBox(): Promise<string> {
    const model = this.boundingBoxModel;
    if (!model || model.targets.length === 0) return "ok: no bounding box to clear";
    model.removeBoundingBoxObject(null);
    return "ok: bounding box cleared";
  }

  /**
   * Put the asked-for visual on the part and report what is ACTUALLY on screen.
   *
   * Returning what is drawn rather than what was requested is the whole point:
   * the agent must never be told a visual is up when it is not, nor that one is
   * down while the user is looking at it.
   */
  private applyMeasureVisual(
    snapshotId: string,
    wantBox: boolean,
    wantRuler: boolean,
  ): { boxShown: boolean; rulerShown: boolean } {
    const model = this.boundingBoxModel;
    if (!model) return { boxShown: false, rulerShown: false };

    // The two are exclusive, so this call resolves to ONE visual - or to none,
    // which is a size question that asked for no box, or a clearance question
    // on a part already resting on the ground.
    const kind: MeasureVisual | null = wantRuler ? "groundRuler" : wantBox ? "box" : null;

    if (!kind) {
      // ⚠ THE AGENT'S PREVIOUS VISUAL COMES DOWN, and that is a change of rule.
      // This tool used to leave a standing box alone on the grounds that it had
      // never taken one down - safe while a box was the only visual, and wrong
      // with two: "leave what is up" means a size answer can inherit the
      // previous question's ruler, or a height answer its box, which is exactly
      // the mismatch the question/picture pairing exists to prevent. A
      // measurement call now OWNS the agent's visual for its turn.
      //
      // Scoped to `origin: "agent"`, so a box the user pinned by hand with the
      // Measure tool is never touched.
      model.clearBoundingBoxes("agent");
      return { boxShown: false, rulerShown: false };
    }

    // Pinning the ALREADY-pinned target is the tool's pin-click toggle and
    // destroys every target (`BoundingBoxToolModel.addBoundingBoxObject`), so
    // asking about the same part twice must not wipe the answer off the screen.
    // An existing pin is switched in place instead.
    if (model.targets.some((t) => t.snapshotId === snapshotId && t.isPersistent)) {
      model.setTargetVisual(snapshotId, kind);
    } else {
      // Pinning drops every other target, so the previous answer's visual on a
      // DIFFERENT part goes with it - no separate clear is needed here.
      model.addBoundingBoxObject(snapshotId, true, "agent", kind);
    }
    return { boxShown: kind === "box", rulerShown: kind === "groundRuler" };
  }

  private get boundingBoxModel(): IBoundingBoxToolModel | null {
    return Ioc.isRegistered<IBoundingBoxToolModel>(BOUNDING_BOX_TOOL_MODEL)
      ? Ioc.resolve<IBoundingBoxToolModel>(BOUNDING_BOX_TOOL_MODEL)
      : null;
  }

  /** The Units dropdown both the ruler and the dimension pills read. */
  private get displayUnit(): KnownUnits {
    return Ioc.isRegistered<IMeasurementsModel>(MEASUREMENTS_MODEL)
      ? Ioc.resolve<IMeasurementsModel>(MEASUREMENTS_MODEL).unit
      : KnownUnits.Meters;
  }

  private async explode(toolName: string, args: ToolArgs): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const isUnexplodeAlias = toolName === AgentToolNames.UnexplodeModel;
    const percentage = isUnexplodeAlias ? 0 : (getNumber(args, "percentage") ?? 50);
    const targetFactor = explosionPercentageToFactor(percentage);

    const paths = getStringList(args, "modelPathsToExplode");
    const entities = this.m_project.getEntities(sceneId);
    const index = buildPathIndex(entities);

    let roots: PathEntry[];
    if (paths.length > 0) {
      const rootPaths = new Set(paths.map((p) => rootPathOf(p)));
      roots = index.filter((e) => e.isRoot && rootPaths.has(e.path));
      if (roots.length === 0) {
        return "error: no models found at the given path(s). Resolve targets with Get_Scene_Context first.";
      }
    } else {
      roots = index.filter((e) => e.isRoot);
      if (roots.length !== 1) {
        return `error: explode needs modelPathsToExplode when the scene has ${roots.length} root models.`;
      }
    }

    roots = roots.filter((root) =>
      this.m_explosion.canExplode(sceneId, root.snapshot.id),
    );
    if (roots.length === 0) {
      return "error: no unlocked multi-part models can be exploded";
    }

    const initialWrites: Array<Promise<number | null>> = [];
    let collectInitialWrites = true;
    const apply = (snapshotId: string, factor: number) => {
      const write = this.m_explosion.commitExplosionFactorAsync(
        sceneId,
        snapshotId,
        factor,
      );
      if (collectInitialWrites) initialWrites.push(write);
      else {
        void write.catch((error: unknown) => {
          console.warn("[EntitiesExecutor] explosion undo/redo failed", error);
        });
      }
    };

    this.m_undoRedo.beginRecordGroup("Explosion");
    try {
      for (const root of roots) {
        const previous = this.m_explosion.getExplosionFactor(
          sceneId,
          root.snapshot.id,
        );
        if (previous === targetFactor) continue;
        this.m_undoRedo.record({
          label: "Explosion",
          redo: () => apply(root.snapshot.id, targetFactor),
          undo: () => apply(root.snapshot.id, previous),
        });
      }
    } finally {
      this.m_undoRedo.endRecordGroup();
      collectInitialWrites = false;
    }
    await Promise.all(initialWrites);

    return `ok: explosion set to ${Math.round(percentage)}% on ${roots.length} model(s)`;
  }

  /**
   * Persistent-effect targets (Unity `ResolvePersistentEffectTargets`): explicit
   * hierarchy paths, with a current-selection fallback when allowed and none
   * given. Shared by the spin and highlight tools, as in Unity.
   */
  private resolvePersistentEffectTargets(
    sceneId: string,
    paths: ReadonlyArray<string>,
    useSelectionFallback: boolean,
  ): PathEntry[] {
    const entities = this.m_project.getEntities(sceneId);
    if (paths.length > 0) return resolveManyByPath(entities, paths);
    if (!useSelectionFallback || this.m_selection.selectedIds.size === 0) return [];
    return buildPathIndex(entities).filter((e) => this.m_selection.isSelected(e.snapshot.id));
  }

  /** Port of Unity `Entities_Executor.HighlightPart` (:3661). */
  private async highlightPart(args: ToolArgs): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const paths = getStringList(args, "partPathsToHighlight");
    const targets = this.resolvePersistentEffectTargets(sceneId, paths, true);
    if (targets.length === 0) return "error: no parts found to highlight";

    const semanticColor = getString(args, "semanticColor");
    const colorHex = getString(args, "colorHex");
    const pulseMode = getString(args, "pulseMode");
    const pulseCount = getNumber(args, "pulseCount");
    const thicknessMode = getString(args, "thicknessMode");
    const hasExplicitColor = isFilled(semanticColor) || isFilled(colorHex);

    // Every target must start its pulse on the SAME frame. Apply the local
    // snapshot tree once, matching Unity's bulk `UpdateEntitiesAsync`, then
    // overlap the cloud row writes. Replacing/rescanning the full tree once per
    // target caused multi-second main-thread stalls on large assemblies.
    const updates: Array<{
      snapshotId: string;
      patch: EntitySnapshotPatch;
    }> = [];

    for (const entry of targets) {
      // Unity copies the EXISTING highlight and overwrites only the fields the
      // call names, so "make it static" keeps the color and thickness.
      const existing = parsePersistentHighlightJson(entry.snapshot.persistentHighlightJson);
      let highlight: PersistentHighlightData = {
        ...(existing ?? DEFAULT_PERSISTENT_HIGHLIGHT),
        enabled: true,
      };

      // A brand-new highlight with no color asked for is hazard (red), not the
      // data default — the tool's own documented default.
      if (!existing && !hasExplicitColor) {
        highlight = { ...highlight, semantic: PersistentHighlightSemantics.Hazard };
      }
      if (isFilled(semanticColor)) {
        highlight = { ...highlight, semantic: normalizeHighlightSemantic(semanticColor) };
      }
      if (isFilled(colorHex)) {
        highlight = { ...highlight, colorHex: normalizeHighlightColorHex(colorHex) };
      }
      if (isFilled(pulseMode)) {
        const mode = normalizeHighlightPulseMode(pulseMode);
        // Switching to continuous without a count means infinite, so an older
        // finite count must not survive the mode change.
        highlight = {
          ...highlight,
          pulseMode: mode,
          pulseCount:
            mode === PersistentHighlightPulseModes.Continuous ? null : highlight.pulseCount,
        };
      }
      if (pulseCount !== null) {
        // Unity reads this as an int and treats <= 0 as "stop pulsing".
        const count = Math.trunc(pulseCount);
        highlight =
          count <= 0
            ? { ...highlight, pulseMode: PersistentHighlightPulseModes.None, pulseCount: null }
            : {
                ...highlight,
                pulseMode: PersistentHighlightPulseModes.Continuous,
                pulseCount: count,
              };
      }
      if (isFilled(thicknessMode)) {
        highlight = {
          ...highlight,
          thicknessMode: normalizeHighlightThicknessMode(thicknessMode),
        };
      }

      updates.push({
        snapshotId: entry.snapshot.id,
        patch: { persistentHighlightJson: serializePersistentHighlight(highlight) },
      });
    }
    await this.m_project.updateEntitySnapshotsAsync(sceneId, updates);
    return `ok: highlighted ${EntitiesExecutor.formatTargets(targets)}`;
  }

  /** Port of Unity `Entities_Executor.ClearPersistentHighlight` (:3762). */
  private async clearHighlight(args: ToolArgs): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const paths = getStringList(args, "partPathsToClearHighlight");
    const hasIdentifiers = paths.length > 0;
    // No paths = clear every highlight in the scene. Deliberately NOT the
    // selection fallback the highlight tool uses (Unity passes
    // `useSelectionFallback: false` and walks `HighlightedObjects` instead).
    const targets = hasIdentifiers
      ? this.resolvePersistentEffectTargets(sceneId, paths, false)
      : buildPathIndex(this.m_project.getEntities(sceneId)).filter(
          (e) => parsePersistentHighlightJson(e.snapshot.persistentHighlightJson) !== null,
        );
    if (targets.length === 0) {
      return hasIdentifiers
        ? "error: no parts found to clear highlight"
        : "ok: no persistent highlights to clear";
    }

    // Cleared together for the same reason the highlight is applied together —
    // a group should not wink out one part per cloud round-trip.
    await this.m_project.updateEntitySnapshotsAsync(
      sceneId,
      targets.map((entry) => ({
        snapshotId: entry.snapshot.id,
        patch: { persistentHighlightJson: null },
      })),
    );
    return `ok: cleared highlight for ${EntitiesExecutor.formatTargets(targets)}`;
  }

  /** Port of Unity `Entities_Executor.ConstantSpinPart` (:3817). */
  private async constantSpin(args: ToolArgs): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const paths = getStringList(args, "partPathsToSpin");
    const targets = this.resolvePersistentEffectTargets(sceneId, paths, true);
    if (targets.length === 0) return "error: no parts found to spin";

    const degreesPerSecond = getNumber(args, "degreesPerSecond");
    if (degreesPerSecond !== null && Math.abs(degreesPerSecond) < 1e-4) {
      return "error: degreesPerSecond must be non-zero";
    }

    const requestedAxis = getString(args, "spinAxis");
    let normalizedAxis: PersistentSpinAxis | null = null;
    if (requestedAxis && requestedAxis.trim().length > 0) {
      normalizedAxis = tryNormalizeSpinAxis(requestedAxis);
      if (!normalizedAxis) return "error: spinAxis must be x, y, or z";
    }

    for (const entry of targets) {
      // Preserve the existing spin's fields unless explicitly overridden
      // (Unity: copy existing, then apply speed/axis when provided).
      const existing = parsePersistentSpinJson(entry.snapshot.persistentSpinJson);
      const spin: PersistentSpinData = {
        enabled: true,
        speedDegreesPerSecond:
          degreesPerSecond
          ?? existing?.speedDegreesPerSecond
          ?? PERSISTENT_SPIN_DEFAULT_SPEED_DEG_PER_SEC,
        axis: normalizedAxis ?? existing?.axis ?? "y",
      };
      await this.m_project.updateEntitySnapshotAsync(sceneId, entry.snapshot.id, {
        persistentSpinJson: serializePersistentSpin(spin),
      });
    }
    return `ok: started spinning ${EntitiesExecutor.formatTargets(targets)}`;
  }

  /** Port of Unity `Entities_Executor.ClearConstantSpin` (:3893). */
  private async clearConstantSpin(args: ToolArgs): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";

    const paths = getStringList(args, "partPathsToClearSpin");
    const hasIdentifiers = paths.length > 0;
    const targets = hasIdentifiers
      ? this.resolvePersistentEffectTargets(sceneId, paths, false)
      : buildPathIndex(this.m_project.getEntities(sceneId)).filter((e) =>
          parsePersistentSpinJson(e.snapshot.persistentSpinJson) !== null,
        );
    if (targets.length === 0) {
      return hasIdentifiers
        ? "error: no parts found to clear spin"
        : "ok: no constant spins to clear";
    }

    for (const entry of targets) {
      // Web deviation from Unity's freeze-at-current-angle: clearing restores
      // the snapshot (authored) rotation — the persisted rotation is the
      // authored one either way, so peers converge identically.
      await this.m_project.updateEntitySnapshotAsync(sceneId, entry.snapshot.id, {
        persistentSpinJson: null,
      });
    }
    return `ok: cleared spin for ${EntitiesExecutor.formatTargets(targets)}`;
  }
}
